import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import { TIME_ZONES, TimeZone } from "@/config";
import { ADD_TREATMENT } from "@/pages/UserDetails/components/UserInfo/components/AddTreatmentModal/queries";
import { LeadOptions } from "@/pages/UserDetails/components/UserInfo/types";
import { Account } from "@/types/api";

import { ONBOARD_USER_SIMPLE, QUERY_RESPONSE } from "../AddUserPopup/queries";
import { FormInputs, OnboardUserResponse, Response } from "../AddUserPopup/types";
import { ActivityPhoneInput, ContactPhoneInput, PhoneEntry } from "./phoneInput";
export const AddUserForm = () => {
  const [showError, setShowError] = useState<boolean>(false);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInputs>();

  const navigate = useNavigate();

  const { data } = useQuery<Response>(QUERY_RESPONSE, {
    fetchPolicy: "network-only",
  });

  const specialists = [...(data?.specialists ?? [])]
    .filter((x) => !!x)
    .sort((a: Account, b: Account) => a.firstName.localeCompare(b.firstName));

  const [onboardUserSimple, { error }] = useMutation<OnboardUserResponse, FormInputs>(
    ONBOARD_USER_SIMPLE
  );
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [addTreatment, { error: treatmentError, loading }] = useMutation(ADD_TREATMENT);

  useEffect(() => {
    if (error || treatmentError) {
      setShowError(true);
    }
  }, [error, treatmentError]);

  const [contactCountry, setContactCountry] = useState("US");
  const [contactPhone, setContactPhone] = useState("");
  const [activityCountry, setActivityCountry] = useState("US");
  const [activityPhone, setActivityPhone] = useState("");

  const onSubmit: SubmitHandler<FormInputs> = async (response) => {
    try {
      const { data } = await onboardUserSimple({
        variables: {
          firstName: response.firstName,
          lastName: response.lastName,
          email: response.email,
          contactPhoneNumber: contactPhone,
          activityPhoneNumber: activityPhone,
          timeZone: response.timeZone,
          specialistId: response.specialistId,
          isTestUser: response.isTestUser,
          leadType: response.leadType,
          Id_Ehr: response.Id_Ehr,
        },
      });

      // Only proceed with addTreatment if onboardUserSimple was successful and returned data
      if (data?.createAccount?.id) {
        await addTreatment({
          variables: {
            program_type: "INTENSIVE",
            start_date: new Date().toISOString(),
            account_id: data.createAccount.id,
            specialist_id: response.specialistId,
          },
        });
        navigate("/refresh");
      } else {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line no-console
        console.error("Failed to create account - no account ID returned");
        setShowError(true);
      }
    } catch (error) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line no-console
      console.error("Failed to onboard user:", error);
      setShowError(true);
    }
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <TextField
            {...register("firstName")}
            autoFocus
            fullWidth
            margin="dense"
            label="First Name"
            placeholder="First Name"
            variant="standard"
            required={true}
          />

          <TextField
            {...register("lastName")}
            fullWidth
            margin="dense"
            label="Last Name"
            placeholder="Last Name"
            variant="standard"
            required={true}
          />
          <TextField
            {...register("Id_Ehr", {
              pattern: {
                value: /^[a-zA-Z0-9]+$/,
                message: "EHR ID must contains only alphanumeric characters",
              },
              required: {
                value: true,
                message: "EHR ID is required",
              },
            })}
            fullWidth
            margin="dense"
            label="EHR Id"
            placeholder="EHR Id"
            variant="standard"
            error={!!errors?.Id_Ehr?.message}
            helperText={errors?.Id_Ehr?.message ?? ""}
            required={true}
          />

          <TextField
            {...register("email", {
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please provide proper format",
              },
            })}
            fullWidth
            margin="dense"
            label="Email"
            placeholder="Email"
            variant="standard"
            required={true}
            error={!!errors?.email?.message}
            helperText={errors?.email?.message ?? ""}
          />

          <br />
          <br />

          <PhoneEntry
            country={contactCountry}
            setCountry={setContactCountry}
            phoneNumber={contactPhone}
            setPhoneNumber={setContactPhone}
            phoneInputComponent={ContactPhoneInput}
          />

          <br />

          <PhoneEntry
            country={activityCountry}
            setCountry={setActivityCountry}
            phoneNumber={activityPhone}
            setPhoneNumber={setActivityPhone}
            phoneInputComponent={ActivityPhoneInput}
          />

          <br />

          <FormControl variant="filled" fullWidth>
            <InputLabel id="timeZoneLabel">Time Zone*</InputLabel>
            <Select {...register("timeZone")} labelId="timeZoneLabel" required={true}>
              {TIME_ZONES.map((timeZone: TimeZone) => (
                <MenuItem key={timeZone.value} value={timeZone.value}>
                  {timeZone.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <br />
          <br />

          <FormControl variant="filled" fullWidth>
            <InputLabel id="specialistLabel">Therapist*</InputLabel>
            <Select {...register("specialistId")} labelId="specialistLabel" required={true}>
              {specialists
                .filter((x) => !!x)
                .map((specialist: Account, index: number) => (
                  <MenuItem key={index} value={specialist.id}>
                    {specialist.firstName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          <br />
          <br />
          <FormControl variant="filled" fullWidth>
            <InputLabel id="leadType">Lead Type</InputLabel>
            <Select {...register("leadType")} labelId="leadType">
              {LeadOptions.map((option, index) => (
                <MenuItem key={index} value={option?.value}>
                  {option?.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <br />
          <br />

          <Typography>
            <label>Is Test Patient</label>
            <Controller
              name="isTestUser"
              defaultValue={false}
              control={control}
              render={({ field }) => (
                <div>
                  <Checkbox
                    onChange={(e) => field.onChange(e.target.checked)}
                    checked={field.value}
                  />
                  <label>Check box if test patient</label>
                </div>
              )}
            />
          </Typography>
        </DialogContent>
        <DialogActions style={{ padding: "20px 20px" }}>
          <Button className="button" type="submit">
            Create Account
          </Button>
        </DialogActions>
      </form>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={showError}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          Failed to onboard user.
        </Alert>
      </Snackbar>
    </>
  );
};
